import { useData } from "../../../runtime/provider";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useDownloads } from "@utils/downloadManager/hooks/useDownloads";
import { useOfflineLibrary } from "@utils/downloadManager/hooks";
import { ReactQueryOptions } from "@utils/trpc";
import type { UseSuspenseQueryOptionsFromConfig } from "@utils/queryConfigs";
import { todaysTopHitsQuery } from "./queryFunctions";

export { todaysTopHitsQueryKey, todaysTopHitsQuery } from "./queryFunctions";

type QueryOptions = Omit<
  ReactQueryOptions["catalog"]["playlists"]["getTodaysTopHits"],
  "suspense"
>;

export const useTodaysTopHits = (options?: QueryOptions) => {
  const data = useData();
  const downloads = useDownloads();
  const rawQuery = useQuery({
    ...todaysTopHitsQuery(data),
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
  });

  // Same offline-image substitution pattern as usePlaylist. Today's top
  // hits has a deterministic id (`<territory>_todays_top_hits`), so the
  // offline match is by id.
  const { offlineLibrary } = useOfflineLibrary();
  const offlineEntry = useMemo(() => {
    if (!offlineLibrary || !rawQuery.data?.id) return undefined;
    return offlineLibrary.find(
      (it) => it.type === "playlist" && it.id === rawQuery.data?.id,
    );
  }, [offlineLibrary, rawQuery.data?.id]);

  useEffect(() => {
    if (rawQuery.data?.id && offlineEntry) {
      downloads.updateOfflineCollection(rawQuery.data.id).catch(console.error);
    }
  }, [rawQuery.data?.id, offlineEntry, downloads]);

  const composedData = useMemo(() => {
    if (!rawQuery.data) return rawQuery.data;
    if (!offlineEntry || offlineEntry.type !== "playlist") return rawQuery.data;
    return { ...rawQuery.data, images: offlineEntry.images };
  }, [rawQuery.data, offlineEntry]);

  const query = useMemo(
    () => ({ ...rawQuery, data: composedData }),
    [rawQuery, composedData],
  ) as typeof rawQuery;

  return query;
};

export const useTodaysTopHitsSuspense = (options?: QueryOptions) => {
  const data = useData();
  const config = todaysTopHitsQuery(data);
  const query = useSuspenseQuery({
    ...config,
    ...(options as UseSuspenseQueryOptionsFromConfig<typeof config>),
  });
  return query;
};
